import { useTranslation } from "react-i18next";
import { History, RotateCw } from "lucide-react";

import { SessionEventsSource, useSessionEvents } from "../../../application/useSessionEvents";
import SessionEventTimeline from "./SessionEventTimeline";

/**
 * A session's timeline with its loading, empty and error states. The same
 * view on the session page and in a centre administrator's agenda panel;
 * `centerId` decides which route reads it.
 */
export default function SessionEventsView(source: SessionEventsSource) {
    const { t } = useTranslation();
    const { events, loading, failed, retry } = useSessionEvents(source);

    if (loading) {
        return (
            <p role="status" className="text-sm text-content-tertiary">
                {t("common.loading")}
            </p>
        );
    }

    if (failed) {
        return (
            <div role="alert" className="flex flex-wrap items-center gap-3 text-sm text-red-600 dark:text-red-400">
                <span>{t("session.events.loadFailed")}</span>
                <button
                    type="button"
                    onClick={retry}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-2.5 py-1 text-xs font-medium text-content-secondary transition-colors hover:border-gray-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:border-white/10"
                >
                    <RotateCw className="h-3.5 w-3.5" aria-hidden="true" />
                    {t("session.events.retry")}
                </button>
            </div>
        );
    }

    if (events.length === 0) {
        return (
            <div className="flex items-start gap-3 text-sm text-content-tertiary">
                <History className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                <div>
                    <p className="font-medium text-content-secondary">{t("session.events.empty")}</p>
                    <p>{t("session.events.emptyHint")}</p>
                </div>
            </div>
        );
    }

    return <SessionEventTimeline events={events} />;
}
